// Формат цены анкеты карты «Evə yaxın» (CC_tutor_cabinet_2026-10-15.md, PR B):
// за урок, в месяц (с числом уроков или без) или за пакет из N уроков.
//
// Правило показа: пока формат не подтверждён самим репетитором
// (price_confirmed_at), показываем только сумму — «60 ₼», без «за урок» и
// «в месяц»: неверная подпись хуже никакой. Исключение — кружки с price_unit
// month: их форма всегда спрашивала «Aylıq qiymət», подпись честная
// (решение владельца 15.10.2026).
//
// Цена за урок (≈) — для сравнения и фильтра карты: известна только у
// подтверждённого формата и только когда число уроков задано (урок — сам
// урок, пакет — всегда, месяц — если указано число уроков в месяце).
//
// Чистые функции: их зовут карта, профиль, форма, кабинет, админка, письмо
// и scripts/verify-tutor-price-format.mts.

export type PriceUnit = "lesson" | "month" | "package";
export const PRICE_UNITS: PriceUnit[] = ["lesson", "month", "package"];
export const isPriceUnit = (v: unknown): v is PriceUnit => v === "lesson" || v === "month" || v === "package";

type Lang = "az" | "ru" | "en";

export type PriceInfo = {
  kind: "tutor" | "club";
  price_azn: number | null;
  price_unit: string;
  lessons_per_unit?: number | null;
  lesson_minutes?: number | null;
  /** Из вида (price_confirmed) или из таблицы (!!price_confirmed_at). undefined — до наката: не подтверждено. */
  price_confirmed?: boolean | null;
};

/** Варианты в форме: чипы числа уроков в месяце и минут урока. */
export const LESSONS_PER_MONTH_CHIPS = [4, 8, 12, 16, 20];
export const LESSON_MINUTES_CHIPS = [45, 60, 90];
/** «Bir dərs» дороже этого — мягкая подсказка «а не за месяц ли?». */
export const LESSON_PRICE_HINT_ABOVE = 40;

/** Показываем ли единицу (за урок / в месяц / за пакет) — см. правило в шапке. */
export function unitShown(p: PriceInfo): boolean {
  if (p.price_confirmed) return true;
  return p.kind === "club" && p.price_unit === "month";
}

/** Цена одного урока для сравнения. null — посчитать нельзя честно. */
export function perLesson(p: PriceInfo): number | null {
  if (p.price_azn === null || !p.price_confirmed) return null;
  if (p.price_unit === "lesson") return p.price_azn;
  const n = p.lessons_per_unit ?? null;
  if ((p.price_unit === "month" || p.price_unit === "package") && n && n > 0) return p.price_azn / n;
  return null;
}

// ─── Слова ────────────────────────────────────────────────────────────

/** Русское множественное число: 1 урок, 2 урока, 5 уроков. */
function ruPlural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b === 1) return one;
  if (b >= 2 && b <= 4) return few;
  return many;
}

/** «урок» у репетитора, «занятие» у кружка (решение владельца 15.10.2026). */
function lessonWord(lang: Lang, kind: PriceInfo["kind"], n: number | null): string {
  const club = kind === "club";
  if (lang === "az") return club ? "məşğələ" : "dərs";
  if (lang === "en") return club ? (n === 1 || n === null ? "class" : "classes") : n === 1 || n === null ? "lesson" : "lessons";
  if (n === null) return club ? "занятие" : "урок";
  return club ? ruPlural(n, "занятие", "занятия", "занятий") : ruPlural(n, "урок", "урока", "уроков");
}

const MONTH: Record<Lang, string> = { az: "ay", ru: "мес", en: "month" };
const MIN: Record<Lang, string> = { az: "dəq", ru: "мин", en: "min" };

/** ≈ цена урока: от 10 ₼ — целыми, ниже — до десятых. */
export function fmtAzn(x: number): string {
  const v = x >= 10 ? Math.round(x) : Math.round(x * 10) / 10;
  return `${v} ₼`;
}

/**
 * Текст цены. main — «15 ₼ / dərs · 60 dəq», «60 ₼ / ay · ayda 12 dərs»,
 * «200 ₼ / 10 dərs», «60 ₼» (не подтверждено). sub — мелко «≈ 5 ₼ / dərs»
 * у месяца с числом уроков и у пакета. null у main — цены нет.
 */
export function priceText(p: PriceInfo, lang: Lang): { main: string | null; sub: string | null } {
  if (p.price_azn === null) return { main: null, sub: null };
  const sum = `${p.price_azn} ₼`;
  if (!unitShown(p)) return { main: sum, sub: null };
  const n = p.lessons_per_unit ?? null;
  const mins = p.price_confirmed && p.lesson_minutes ? ` · ${p.lesson_minutes} ${MIN[lang]}` : "";
  const one = lessonWord(lang, p.kind, null);
  const approx = perLesson(p);
  const sub = approx !== null && p.price_unit !== "lesson" ? `≈ ${fmtAzn(approx)} / ${one}` : null;
  if (p.price_unit === "lesson") return { main: `${sum} / ${one}${mins}`, sub: null };
  if (p.price_unit === "package" && n) return { main: `${sum} / ${n} ${lessonWord(lang, p.kind, n)}${mins}`, sub };
  if (p.price_unit === "month") {
    const per = p.price_confirmed && n ? (lang === "az" ? ` · ayda ${n} ${one}` : lang === "ru" ? ` · ${n} ${lessonWord(lang, p.kind, n)} в месяц` : ` · ${n} ${lessonWord(lang, p.kind, n)} a month`) : "";
    return { main: `${sum} / ${MONTH[lang]}${per}${mins}`, sub };
  }
  return { main: sum, sub: null };
}

/**
 * Подпись на метке карты — коротко, с единицей, как в карточке: «80 ₼/ay»,
 * «15 ₼/dərs», «200 ₼/10 dərs». Формат не подтверждён — только сумма «60 ₼»
 * (то же правило, что у priceText). null — цены нет.
 */
export function pinPrice(p: PriceInfo, lang: Lang): string | null {
  if (p.price_azn === null) return null;
  const sum = `${p.price_azn} ₼`;
  if (!unitShown(p)) return sum;
  const n = p.lessons_per_unit ?? null;
  if (p.price_unit === "lesson") return `${sum}/${lessonWord(lang, p.kind, null)}`;
  if (p.price_unit === "month") return `${sum}/${MONTH[lang]}`;
  if (p.price_unit === "package" && n) return `${sum}/${n} ${lessonWord(lang, p.kind, n)}`;
  return sum;
}

/** Одной строкой (админка, письмо нам, JSON-LD): main и sub через пробел. */
export const priceLine = (p: PriceInfo, lang: Lang) => {
  const t = priceText(p, lang);
  return t.main === null ? null : t.sub ? `${t.main} (${t.sub})` : t.main;
};

/**
 * Фильтр карты «цена урока до» (вариант A, решение владельца 15.10.2026):
 * известная цена урока выше порога — анкета скрыта; неизвестная — анкета
 * остаётся, но идёт после остальных, под подписью «цена урока не указана».
 */
export function priceFilter(p: PriceInfo, max: number | null): "ok" | "over" | "unknown" {
  if (max === null) return "ok";
  const v = perLesson(p);
  if (v === null) return "unknown";
  return v <= max ? "ok" : "over";
}
